import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, Info, CheckCircle, AlertCircle, ExternalLink, RefreshCw, Globe, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { getOfficialVisaUrl, USA_VWP_COUNTRIES } from "@/utils/officialVisaUrls";
import { getVisaOnArrivalInfo, isEligibleForVOA } from "@/utils/visaOnArrivalData";
import { MultiCountryVisaPlanner } from "./MultiCountryVisaPlanner";
import { useSubscription } from "@/hooks/useSubscription";
import { useFeatureUsage } from "@/hooks/useFeatureUsage";
import { UpgradePrompt } from "@/components/upgrade/UpgradePrompt";
import { useNavigate } from "react-router-dom";

interface VisaCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  destination: string;
  destinationCountry: string;
}

interface VisaRequirement {
  required: boolean;
  type?: string;
  duration?: string;
  processingTime?: string;
  notes?: string;
  url?: string;
  lastUpdated?: string;
  error?: string;
}

// Normalize Supabase function result to a VisaRequirement
function parseVisaData(data: any): VisaRequirement {
  try {
    const obj = typeof data === 'string' ? JSON.parse(data) : data;
    if (obj && typeof obj === 'object') {
      // Some runtimes wrap result in { result } or { data }
      const payload = (obj as any).result ?? (obj as any).data ?? obj;

      // If content is a string blob, try to extract JSON
      if (typeof (payload as any)?.content === 'string') {
        const match = (payload as any).content.match(/\{[\s\S]*\}/);
        if (match) return JSON.parse(match[0]);
      }

      const typeStr = String((payload as any)?.type ?? '').toLowerCase();
      const notesStr = String((payload as any)?.notes ?? '').toLowerCase();
      const hasProvidedRequired = typeof (payload as any)?.required === 'boolean';

      // Prefer recognizing online authorizations (eVisa/ESTA/ETA) even when text also says "visa-free"
      const requiresOnlineAuth = /(e-?visa|e\s?visa|esta|e-?ta|eta|electronic travel authorization|apply online)/i.test(typeStr)
        || /(e-?visa|e\s?visa|esta|e-?ta|eta|electronic travel authorization|apply online)/i.test(notesStr);

      // Infer `required` when missing using prioritized rules
      let inferredRequired: boolean;
      if (hasProvidedRequired) {
        inferredRequired = (payload as any).required as boolean;
      } else if (requiresOnlineAuth) {
        // Treat online authorizations as "required" so we surface the CTA
        inferredRequired = true;
      } else if (/(visa[-\s]?free|no visa|visa not required)/i.test(typeStr) || /(visa[-\s]?free|no visa|visa not required)/i.test(notesStr)) {
        inferredRequired = false;
      } else if (/(visa required|entry permit|visa)/i.test(typeStr) || /(visa required|entry permit|visa)/i.test(notesStr)) {
        inferredRequired = true;
      } else {
        inferredRequired = true; // safe default
      }

      return {
        required: inferredRequired,
        type: (payload as any)?.type,
        duration: (payload as any)?.duration,
        processingTime: (payload as any)?.processingTime,
        notes: (payload as any)?.notes,
        url: (payload as any)?.url,
        lastUpdated: (payload as any)?.lastUpdated ?? new Date().toISOString().split('T')[0],
        error: (payload as any)?.error,
      } as VisaRequirement;
    }
  } catch {}

  return {
    required: true,
    type: 'Unable to retrieve visa information',
    duration: 'See official source',
    processingTime: 'See official source',
    notes: 'Unable to retrieve specific visa details at this time. Please click the link below to check the official government website for accurate visa requirements, costs, and processing times.',
    url: 'https://www.google.com/search?q=visa+requirements',
    lastUpdated: new Date().toISOString().split('T')[0],
    error: 'PARSE_ERROR',
  } as VisaRequirement;
}

// Comprehensive list of countries (193 UN member states + major territories)
const passportCountries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana',
  'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon',
  'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo',
  'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Democratic Republic of the Congo',
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
  'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea',
  'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia',
  'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon',
  'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
  'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique',
  'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger',
  'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
  'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
  'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga',
  'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
  'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
].sort();

export function VisaCheckModal({ isOpen, onClose, destination, destinationCountry }: VisaCheckModalProps) {
  const [selectedPassport, setSelectedPassport] = useState<string>('');
  const [visaInfo, setVisaInfo] = useState<VisaRequirement | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [activeTab, setActiveTab] = useState<'single' | 'multi'>('single');
  const { subscription } = useSubscription();
  const { recordUsage } = useFeatureUsage();
  const navigate = useNavigate();
  
  const isFirstClass = subscription.subscription_tier === 'first_class';

  // Simple local cache with 24-hour expiry for faster subsequent checks
  const cacheKey = (passport: string, dest: string) => `visa_req:${passport}|${dest}`;
  const loadFromCache = (passport: string, dest: string): VisaRequirement | null => {
    try {
      const raw = localStorage.getItem(cacheKey(passport, dest));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const maxAge = 1000 * 60 * 60 * 24; // 24 hours for up-to-date info
      if (!parsed?.data || !parsed?.cachedAt || Date.now() - parsed.cachedAt > maxAge) return null;
      return parsed.data as VisaRequirement;
    } catch {
      return null;
    }
  };
  const saveToCache = (passport: string, dest: string, data: VisaRequirement) => {
    try {
      localStorage.setItem(cacheKey(passport, dest), JSON.stringify({ data, cachedAt: Date.now() }));
    } catch {}
  };

  // Deterministic regional overrides: EU/Schengen freedom of movement
  const EU_SCHENGEN = new Set([
    'Austria','Belgium','Bulgaria','Croatia','Cyprus','Czech Republic','Denmark','Estonia','Finland','France','Germany','Greece','Hungary','Ireland','Italy','Latvia','Lithuania','Luxembourg','Malta','Netherlands','Poland','Portugal','Romania','Slovakia','Slovenia','Spain','Sweden','Iceland','Liechtenstein','Norway','Switzerland'
  ]);

  const applyRegionalOverrides = (info: VisaRequirement, passport: string, dest: string): VisaRequirement => {
    const p = String(passport).trim();
    const d = String(dest).trim();
    const dLower = d.toLowerCase();

    // EU/Schengen freedom of movement
    if (EU_SCHENGEN.has(p) && EU_SCHENGEN.has(d)) {
      return {
        ...info,
        required: false,
        type: 'Visa-free (EU/Schengen freedom of movement)',
        notes: 'EU/Schengen citizens can travel visa-free within the area. Carry a valid national ID or passport.',
        url: info.url ?? 'https://european-union.europa.eu/live-work-study/travel-and-move-around-eu_en',
      };
    }

    // USA Visa Waiver Program (ESTA) - special handling for VWP countries
    const isUSDestination =
      d === 'United States' ||
      d === 'USA' ||
      d === 'US' ||
      d === 'United States of America' ||
      dLower.includes('united states') ||
      dLower.includes('usa');

    if (isUSDestination && USA_VWP_COUNTRIES.has(p)) {
      return {
        ...info,
        required: true,
        type: 'ESTA (Electronic System for Travel Authorization)',
        duration: 'Up to 90 days per visit',
        processingTime: 'Usually approved within minutes to 72 hours',
        notes:
          'ESTA authorization required before travel. Cost: $40 USD. Valid for 2 years or until passport expires. Apply at least 72 hours before departure. Passport must be valid for duration of stay. ESTA is for tourism, business, or transit only.',
        url: 'https://esta.cbp.dhs.gov/',
      };
    }

    // Check for official visa URL from centralized configuration
    const officialUrl = getOfficialVisaUrl(d);
    if (officialUrl) {
      return {
        ...info,
        url: officialUrl,
      };
    }

    return info;
  };
  const checkVisaRequirement = async () => {
    if (!selectedPassport) {
      toast.error("Please select your passport country");
      return;
    }

    // Check and record usage for free tier users
    if (subscription.subscription_tier === 'economy') {
      const canUse = await recordUsage('visa_check');
      if (!canUse) {
        return; // Usage limit reached, toast already shown
      }
    }

    // Show cached result instantly if available (but always fetch fresh data)
    const cached = loadFromCache(selectedPassport, destinationCountry);
    if (cached) {
      setVisaInfo(applyRegionalOverrides(cached, selectedPassport, destinationCountry));
    }

    setIsChecking(true);
    setIsRefining(false);

    const timeoutMs = 12000; // 12 seconds for accurate data

    try {
      const result = await Promise.race<any>([
        supabase.functions.invoke('visa-requirements', {
          body: {
            passportCountry: selectedPassport,
            destinationCountry: destinationCountry,
          },
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('CLIENT_TIMEOUT')), timeoutMs)
        ),
      ]);

      const { data, error } = result as { data: any; error: any };

      if (error) {
        console.error('Visa check error:', error);
        throw error;
      }

      let parsedData = parseVisaData(data);
      parsedData = applyRegionalOverrides(parsedData, selectedPassport, destinationCountry);
      setVisaInfo(parsedData);
      saveToCache(selectedPassport, destinationCountry, parsedData);
    } catch (error: any) {
      console.error('Error checking visa requirements:', error);

      if (error?.message === 'CLIENT_TIMEOUT') {
        toast.error('Request took too long. Please try again or use the link below.');
      } else {
        toast.error('Unable to fetch visa requirements');
      }

      // Fallback only if no cached data
      if (!cached) {
        const fallback: VisaRequirement = {
          required: true,
          type: 'Unable to retrieve current requirements',
          duration: 'Check official source for specific details',
          processingTime: 'Check official source for processing times',
          notes: `Unable to retrieve specific visa details at this time. Please visit the official ${destinationCountry} immigration website below for accurate requirements including exact costs, maximum stay duration, and current processing times for ${selectedPassport} passport holders.`,
          url: getOfficialVisaUrl(destinationCountry) || `https://www.google.com/search?q=${encodeURIComponent(destinationCountry + ' visa requirements ' + selectedPassport + ' official site:.gov')}`,
          lastUpdated: new Date().toISOString().split('T')[0],
        };
        setVisaInfo(applyRegionalOverrides(fallback, selectedPassport, destinationCountry));
      }
    } finally {
      setIsChecking(false);
    }
  };

  const resetModal = () => {
    setSelectedPassport('');
    setVisaInfo(null);
    setIsChecking(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Visa Requirements Check
          </DialogTitle>
          <DialogDescription>
            Check visa requirements for your destination
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'single' | 'multi')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Single Country
            </TabsTrigger>
            <TabsTrigger value="multi" className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Multi-Country
              {!isFirstClass && <Lock className="h-3 w-3" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select your passport country:</label>
              <Select value={selectedPassport} onValueChange={setSelectedPassport}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose your passport country" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {passportCountries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* VOA Indicator */}
            {selectedPassport && isEligibleForVOA(destinationCountry, selectedPassport) && (
              <Alert className="bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-900">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <AlertDescription className="text-green-800 dark:text-green-200">
                  <div className="space-y-2">
                    <p className="font-semibold">Visa on Arrival Available! ✈️</p>
                    {(() => {
                      const voaInfo = getVisaOnArrivalInfo(destinationCountry);
                      if (!voaInfo) return null;
                      return (
                        <div className="text-sm space-y-1">
                          <p><strong>Cost:</strong> {voaInfo.cost?.amount} {voaInfo.cost?.currency}</p>
                          <p><strong>Max Stay:</strong> {voaInfo.maxStayDays} days</p>
                          {voaInfo.airports && voaInfo.airports.length > 0 && (
                            <p><strong>Available at:</strong> {voaInfo.airports.slice(0, 2).join(', ')}
                              {voaInfo.airports.length > 2 && ` +${voaInfo.airports.length - 2} more`}
                            </p>
                          )}
                          {voaInfo.requirements.returnTicket && (
                            <p className="text-xs">• Return ticket required</p>
                          )}
                          {voaInfo.requirements.hotelBooking && (
                            <p className="text-xs">• Hotel booking required</p>
                          )}
                          {voaInfo.requirements.acceptedPaymentMethods && (
                            <p className="text-xs">• Payment: {voaInfo.requirements.acceptedPaymentMethods.join(', ')}</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {selectedPassport && (
              <div className="space-y-2">
                <Button 
                  onClick={checkVisaRequirement} 
                  disabled={isChecking && !visaInfo}
                  className="w-full"
                >
                  {isChecking ? (
                    <>
                      <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                      {visaInfo ? 'Refreshing Latest Requirements…' : 'Checking Latest Requirements...'}
                    </>
                  ) : (
                    'Check Current Visa Requirements'
                  )}
                </Button>
                {visaInfo && !isChecking && (
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={checkVisaRequirement}
                    className="w-full"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Refresh Data
                  </Button>
                )}
              </div>
            )}

          {isChecking && (
            <div className="py-2">
              <LoadingSpinner size="sm" text={visaInfo ? 'Refreshing latest visa rules…' : 'Fetching latest visa rules...'} />
            </div>
          )}
          {isRefining && !isChecking && (
            <div className="text-xs text-muted-foreground">Showing quick result. Refining details…</div>
          )}

          {visaInfo && (
            <div className="space-y-4">
              {visaInfo.required ? (
                <Alert variant="default" className="border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950">
                  <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                  <AlertDescription className="text-orange-800 dark:text-orange-200">
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-orange-900 dark:text-orange-100 mb-2">Visa Required</p>
                        <div className="space-y-1 text-sm">
                          <p><strong>Type:</strong> {visaInfo.type}</p>
                          <p><strong>Maximum Stay:</strong> {visaInfo.duration}</p>
                          <p><strong>Processing Time:</strong> {visaInfo.processingTime}</p>
                        </div>
                      </div>
                      
                      {visaInfo.notes && (
                        <div className="pt-3 border-t border-orange-200 dark:border-orange-800">
                          <p className="text-sm whitespace-pre-line">{visaInfo.notes}</p>
                        </div>
                      )}
                      
                      {visaInfo.url && (
                        <div className="pt-3 border-t border-orange-200 dark:border-orange-800">
                          <Button className="w-full" asChild>
                            <a
                              href={visaInfo.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              {visaInfo.url.includes('.gov') || visaInfo.url.includes('embassy')
                                ? 'Apply for Visa (Official Site)'
                                : 'Check Visa Requirements'}
                            </a>
                          </Button>
                          {(visaInfo.url.includes('.gov') || visaInfo.url.includes('embassy')) && (
                            <div className="flex items-center justify-center gap-1 mt-2 text-xs text-green-700 dark:text-green-400">
                              <CheckCircle className="h-3 w-3" />
                              <span>Official government website</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="default" className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertDescription className="text-green-800 dark:text-green-200">
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-green-900 dark:text-green-100 mb-2">No Visa Required</p>
                        <div className="space-y-1 text-sm">
                          <p><strong>Entry Type:</strong> {visaInfo.type}</p>
                          <p><strong>Maximum Stay:</strong> {visaInfo.duration}</p>
                        </div>
                      </div>
                      
                      {visaInfo.notes && (
                        <div className="pt-3 border-t border-green-200 dark:border-green-800">
                          <p className="text-sm whitespace-pre-line">{visaInfo.notes}</p>
                        </div>
                      )}
                      
                      {visaInfo.url && (
                        <div className="pt-3 border-t border-green-200 dark:border-green-800">
                          <Button variant="outline" className="w-full" asChild>
                            <a
                              href={visaInfo.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              View Official Information
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {visaInfo.lastUpdated && (
                <p className="text-xs text-muted-foreground text-center">
                  Information as of: {new Date(visaInfo.lastUpdated).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Important:</strong> Always verify with official government sources or embassy websites before traveling. Visa requirements can change, and processing times may vary.
            </p>
          </div>

          <Button variant="outline" onClick={handleClose} className="w-full">
            Close
          </Button>
          </TabsContent>

          <TabsContent value="multi" className="space-y-4">
            {!isFirstClass ? (
              <div className="space-y-4">
                <UpgradePrompt
                  feature="multi_country_visa_planner"
                  requiredTier="first_class"
                  title="Plan Multi-Country Trips Like a Pro"
                  description="Save time and money with intelligent visa route optimization"
                  benefits={[
                    "Optimize visit order to minimize visa costs",
                    "See total expenses and processing times upfront",
                    "Never miss visa requirements for complex itineraries"
                  ]}
                >
                  <div />
                </UpgradePrompt>
                <Button variant="outline" onClick={handleClose} className="w-full">
                  Close
                </Button>
              </div>
            ) : selectedPassport ? (
              <MultiCountryVisaPlanner passportCountry={selectedPassport} onClose={handleClose} />
            ) : (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <p className="text-sm">
                    Please select your passport country from the "Single Country" tab first to access multi-country planning.
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}